/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2025, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import { Logger } from '@core/lib/services';
import { IntegrationFactoryService } from './integration-factory.service';
const log = new Logger('ViewerDecorators');

export type ViewerMethod = 'home' | 'open' | 'close' | 'closeAll' | 'logout' | '';

const methodsMap = {
  launchreadonly: 'open',
  read: 'open',
  preview: 'home'
};
const closeMethodsMap = {
  launchreadonly: 'close',
  read: 'close',
  preview: 'home'
};

// cache viewerService
let viewerService: any = null;

/**
 * ViewerPreAction is a method decorator that dynamically determines and invokes
 * the appropriate method on the `viewerService` instance based on the provided
 * `methodName` and the context of the arguments passed to the decorated method.
 *
 * ### Important:
 * Keep the Injector Service as public injector available in this scope of Decorated method
 *
 * ### Parameters:
 * - `methodName` (`ViewerMethod`): The name of the method to be invoked on the `viewerService` instance.
 * - if `methodName` not provided or empty, it will look for the examContext property in arguments of decorated method.
 *
 * ### Example Usage:
 * ```typescript
 * @ViewerPreAction(ViewerMethod)
 * async someMethod(args: any, tabContext?:string) {
 *   // Your method logic here
 * }
 * ```
 */

export function ViewerPreAction(methodName: ViewerMethod): MethodDecorator {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value;
    descriptor.value = async function (...args: any[]) {
      try {
        // Use cached instance if available, otherwise inject and cache it
        if (!viewerService) {
          const integrationFactoryService = this.injector.get(IntegrationFactoryService);
          viewerService = await integrationFactoryService.getViewer();
        }

        const [tabSwitchEvent, tabCloseEvent] = args;

        if (tabCloseEvent?.examContext) {
          methodName = closeMethodsMap[tabCloseEvent.examContext] || methodName;
          if (tabCloseEvent?.active === false && methodName === 'home') {
            return originalMethod.apply(this, args);
          }
        } else if (tabSwitchEvent?.examContext) {
          methodName = methodsMap[tabSwitchEvent?.examContext];
        }

        if (!(viewerService && viewerService[methodName] instanceof Function)) {
          log.warn(
            `${methodName} is not available`,
            `There is no pre check for ${methodName} method defined in viewer service`
          );
          return originalMethod.apply(this, args);
        }

        let response = true;
        switch (methodName) {
          case 'home':
          case 'open':
            response = await viewerService.home(tabCloseEvent?.isAutoAdvance);
            break;
          case 'close':
            if (tabCloseEvent?.active === false) {
              viewerService.close(args[0]);
              response = true;
              log.debug(`Skipping inactive tab blocking: Tab: ${tabCloseEvent.id}, Close Response: ${response}`);
            } else {
              response = await viewerService.close(args[0], tabCloseEvent?.isAutoAdvance);
            }
            break;
          case 'logout':
            response = await viewerService.logout();
            break;
          case 'closeAll':
            response = await viewerService.closeAll();
            break;
        }

        if (!response) {
          log.debug(`${methodName}: returned false`);
          return;
        }
        return originalMethod.apply(this, args);
      } catch (error) {
        log.error(`Error response for ${methodName} : Execution failed in decorated method ${propertyKey}`, error);
        return;
      }
    };

    return descriptor;
  };
}

// Expose a method to reset the viewerService for testing purposes
(ViewerPreAction as any).resetViewerService = function () {
  viewerService = null;
};

export function PreAction(methodName: 'clearContext' | 'close'): MethodDecorator {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value;
    descriptor.value = async function (...args: any[]) {
      try {
        const risiService = await this.injector.get(IntegrationFactoryService).getRISIService();
        const [_context, tabContext] = args;

        if (tabContext?.examContext && tabContext?.active === false) {
          return originalMethod.apply(this, args);
        }

        if (!(risiService && risiService[methodName] instanceof Function)) {
          log.warn(
            `${methodName} is not available`,
            `There is no pre check for ${methodName} method defined in service`
          );
          return originalMethod.apply(this, args);
        }

        await risiService[methodName](tabContext);
      } catch (error) {
        log.error(`Error response for ${methodName}: Execution failed in decorated method ${propertyKey}`, error);
      }
      return originalMethod.apply(this, args);
    };

    return descriptor;
  };
}
